import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "bg-surface-2/60 border-metallic text-white placeholder:text-slate-500";
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif", "image/bmp", "image/tiff", "image/heic", "image/heif"];

export default function VehicleForm({ initial, onSubmit, onCancel, submitting }) {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState(() => (initial?.images ? [...initial.images] : []));
  const [form, setForm] = useState(() =>
    initial
      ? {
          title: initial.title,
          vtype: initial.vtype,
          price: initial.price,
          year: initial.year,
          status: initial.status,
          description: initial.description || "",
          specs: initial.specs || {},
        }
      : {
          title: "",
          vtype: "husbil",
          price: "",
          year: "",
          status: "tillganglig",
          description: "",
          specs: {},
        }
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const spec = (k) => form.specs[k] || "";
  const setSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));

  const handleFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;
    if (imageUrls.length + picked.length > 20) {
      toast.error("Max 20 bilder per fordon.");
      return;
    }
    setUploading(true);
    for (const file of picked) {
      if (!ALLOWED.includes(file.type)) {
        toast.error(`${file.name}: ogiltigt filformat — endast bilder tillåts`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: max 10 MB per bild`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("photo", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setImageUrls((urls) => [...urls, data.url]);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    setUploading(false);
  };

  const removeImage = (i) => {
    setImageUrls((urls) => urls.filter((_, idx) => idx !== i));
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      title: form.title,
      vtype: form.vtype,
      price: Number(form.price) || 0,
      year: Number(form.year) || new Date().getFullYear(),
      status: form.status,
      description: form.description,
      images: imageUrls,
      specs: Object.fromEntries(Object.entries(form.specs).filter(([, v]) => v)),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="vehicle-form">
      <div className="space-y-1.5">
        <Label htmlFor="vf-title" className="text-slate-300">Titel *</Label>
        <Input id="vf-title" data-testid="vehicle-form-title" required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="T.ex. Knaus Südwind 500 QDK" className={inputCls} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-slate-300">Typ *</Label>
          <Select value={form.vtype} onValueChange={(v) => set("vtype", v)}>
            <SelectTrigger data-testid="vehicle-form-type" className={`${inputCls} w-full`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface border-metallic text-white">
              <SelectItem value="husbil">Husbil</SelectItem>
              <SelectItem value="husvagn">Husvagn</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300">Status *</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="vehicle-form-status" className={`${inputCls} w-full`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface border-metallic text-white">
              <SelectItem value="tillganglig">Tillgänglig</SelectItem>
              <SelectItem value="salt">Såld</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="vf-price" className="text-slate-300">Pris (kr) *</Label>
          <Input id="vf-price" data-testid="vehicle-form-price" type="number" min="0" required value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-year" className="text-slate-300">Årsmodell *</Label>
          <Input id="vf-year" data-testid="vehicle-form-year" type="number" min="1950" max="2100" required value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="vf-beds" className="text-slate-300">Sovplatser</Label>
          <Input id="vf-beds" data-testid="vehicle-form-beds" value={spec("Sovplatser")} onChange={(e) => setSpec("Sovplatser", e.target.value)} placeholder="4" className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-length" className="text-slate-300">Längd</Label>
          <Input id="vf-length" data-testid="vehicle-form-length" value={spec("Längd")} onChange={(e) => setSpec("Längd", e.target.value)} placeholder="6,62 m" className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-weight" className="text-slate-300">Totalvikt</Label>
          <Input id="vf-weight" data-testid="vehicle-form-weight" value={spec("Totalvikt")} onChange={(e) => setSpec("Totalvikt", e.target.value)} placeholder="1 500 kg" className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-mileage" className="text-slate-300">Mätarställning</Label>
          <Input id="vf-mileage" data-testid="vehicle-form-mileage" value={spec("Mätarställning")} onChange={(e) => setSpec("Mätarställning", e.target.value)} placeholder="31 200 km" className={inputCls} />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="vf-fuel" className="text-slate-300">Drivmedel</Label>
          <Input id="vf-fuel" data-testid="vehicle-form-fuel" value={spec("Drivmedel")} onChange={(e) => setSpec("Drivmedel", e.target.value)} placeholder="Diesel" className={inputCls} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-slate-300">Bilder (dra och släpp, max 20)</Label>
        <div
          data-testid="vehicle-form-dropzone"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
            dragOver ? "border-electric bg-brand/10" : "border-metallic bg-surface-2/40 hover:border-brand/60"
          }`}
        >
          <UploadCloud size={28} className={`mx-auto mb-2 ${uploading ? "animate-pulse text-electric" : "text-slate-400"}`} />
          <p className="text-sm font-medium text-slate-300">
            {uploading ? "Laddar upp bilderna..." : "Dra bilderna hit — eller klicka för att välja"}
          </p>
          <p className="mt-1 text-xs text-slate-500">JPG, PNG eller WebP · max 10 MB per bild · upp till 20 st</p>
          <input
            ref={fileRef}
            data-testid="vehicle-form-file-input"
            type="file"
            accept={ALLOWED.join(",")}
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
        {imageUrls.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-3" data-testid="vehicle-form-previews">
            {imageUrls.map((url, i) => (
              <div key={`${url}-${i}`} className="relative rounded-lg overflow-hidden border border-metallic">
                <img src={url} alt={`Bild ${i + 1}`} className="aspect-[4/3] w-full object-cover" data-testid={`vehicle-form-thumb-${i}`} />
                <button
                  type="button"
                  data-testid={`vehicle-form-thumb-remove-${i}`}
                  onClick={() => removeImage(i)}
                  className="absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink/90 text-slate-300 hover:text-white"
                  aria-label="Ta bort bild"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="vf-desc" className="text-slate-300">Beskrivning</Label>
        <Textarea id="vf-desc" data-testid="vehicle-form-description" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="submit" data-testid="vehicle-form-submit" disabled={submitting} className="flex-1 rounded-full bg-brand hover:bg-brand-dark text-white font-semibold">
          {submitting ? "Sparar..." : initial ? "Spara ändringar" : "Lägg till fordon"}
        </Button>
        {onCancel && (
          <Button type="button" data-testid="vehicle-form-cancel" variant="outline" onClick={onCancel} className="rounded-full border-metallic text-slate-300 hover:text-white">
            Avbryt
          </Button>
        )}
      </div>
    </form>
  );
}
